import dayjs from 'dayjs'
import { ChevronLeft, ChevronRight, LucidePlusCircle, RefreshCw, ShoppingBag } from 'lucide-react'
import Image from 'next/image'
import { useRouter } from 'next/router'
import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useAuth } from '~/components/Auth/AuthProvider'
import { logout } from '~/components/Auth/Logout'
import { NewBuildBanner } from '~/components/Common/NewBuildBanner'
import { Spacer } from '~/components/Common/Spacer'
import { DropdownWrapper } from '~/components/Dropdown/DropdownWrapper'
import Icon from '~/components/Icon'
import { Sidebar } from '~/components/Sidebar/Sidebar'
import { SidebarListItem } from '~/components/Sidebar/SidebarListItem'
import { SidebarListSubitem } from '~/components/Sidebar/SidebarListSubitem'
import { useSubscription } from '~/components/Subscription/SubscriptionProvider'
import { Button } from '~/components/ui/button'
import { Select, SelectContent, SelectItem, SelectSeparator, SelectTrigger, SelectValue } from '~/components/ui/select'
import { responsiveBreakpointMap } from '~/constants'
import { COLORS } from '~/constants/colors'
import $posthog from '~/lib/posthog'
import $sentry from '~/lib/sentry'
import locale from '~/locale'
import { setCurrencyState } from '~/redux/slices/currencySlice'
import { RootState } from '~/redux/store'
import { delayedCallback, getCurrentBusiness, setCurrentBusiness } from '~/utils'
import { DesktopPrompt } from '../DesktopPrompt'

interface DashboardProps {
    /**
     * Extend element
     */
    children: JSX.Element | JSX.Element[]
    /**
     * Curent route
     */
    pathname?: string
    /**
     * Hide navigation
     */
    hideNavigation?: boolean
    /**
     * Show Mobile version
     */
    showMobile?: boolean
}



export const Dashboard = ({ children, pathname = '', hideNavigation, showMobile = false }: DashboardProps) => {
    const language = useSelector((state: RootState) => state.language.language)
    // Assuming locale has a specific type, e.g. LocaleType
    const localeText = (locale[language as keyof typeof locale] || locale.en) as any
    const { profile, businesses, currentBusiness, checkAccess } = useAuth()

    const { execFeature, getSubscription, openPaywall, isFreeTimeLimitExceeded } = useSubscription()
    const [active, setActive] = useState(false)
    const [isMounted, setIsMounted] = useState(false)
    const router = useRouter()
    const parentRoute = '/' + pathname.split('/')[2]
    const [showMobileSidebar] = useState(false)
    const [profileDropdownState, setProfileDropdownState] = useState(false)

    useEffect(() => {
        setIsMounted(true)
    }, [])



    const navigationMenu = useMemo(() => {
        if (!isMounted) return []

        return [
            {
                text: localeText?.common?.dashboard,
                slug: '/dashboard',
                icon: <Icon name="home-outline" width={18} height={18}></Icon>
            },
            {
                text: localeText?.report?.title,
                slug: '/product-daily-movement',
                icon: <Icon name="report" width={18} height={18}></Icon>,
                submenu: [
                    {
                        text: localeText?.NewReportLang?.DM,
                        slug: "/product-daily-movement",
                    },

                    {
                        text: localeText?.short?.finance,
                        slug: '/report',
                        icon: <Icon name="money-outline" width={18} height={18}></Icon>
                    },
                    {
                        text: localeText?.short?.stock,
                        slug: '/stock-report',
                        icon: <Icon name="report" width={18} height={18}></Icon>
                    },
                    {
                        text: localeText?.common?.customer,
                        slug: '/customer-report'
                    },
                    {
                        text: localeText?.dailyCashReport?.title || "Daily Cash Report",
                        slug: '/daily-cash-report'
                    },
                    ...(currentBusiness()?.isWarehouseAvailable &&
                        !currentBusiness()?.roles?.includes("staff")
                        ? [
                            {
                                text: localeText?.NewReportLang?.WR,
                                slug: "/warehouse-report",
                            },
                        ]
                        : []),
                ]
            },
            {
                text: localeText?.common?.product,
                slug: '/product',
                icon: <Icon name="archive-outline" width={18} height={18}></Icon>
            },
            {
                text: localeText?.common?.stock_in,
                slug: '/stock-in',
                icon: <Icon name="download" width={18} height={18}></Icon>
            },
            {
                text: localeText?.common?.stock_out,
                slug: '/stock-out',
                icon: <Icon name="upload" width={18} height={18}></Icon>
            },
            {
                text: !active ? localeText?.PurchaseSales?.PurchaseOrder : localeText?.PurchaseSales?.purchase,
                slug: '/purchase',
                icon: <Icon name="purchase-sales-icon" width={18} height={18}></Icon>
            },
            {
                text: !active ? localeText?.PurchaseSales?.SalesOrder : localeText?.PurchaseSales?.sales,
                slug: '/sales',
                icon: <Icon name="salesorderI" width={18} height={18}></Icon>
            },
            {
                text: !active ? (localeText?.catalog?.catalogOrders || "Catalog Orders") : (localeText?.catalog?.catalogOrders || "Orders"),
                slug: '/catalog-orders',
                icon: <ShoppingBag size={18} />
            },
            {
                text: localeText?.common?.history,
                slug: '/history',
                icon: <Icon name="history" width={18} height={18}></Icon>
            },
            {
                text: !active ? localeText?.BatchLocal?.Batchmanagement : localeText?.phoneInvoiceDownload?.Batch,
                slug: '/batch',
                icon: <Icon name="batchm" width={18} height={18}></Icon>
            },
            {
                text: localeText?.common?.contact,
                slug: '/contact',
                icon: <Icon name="user-group" width={18} height={18}></Icon>,
                submenu: [
                    {
                        text: localeText?.common?.customer,
                        slug: '/customer'
                    },
                    {
                        text: localeText?.common?.supplier,
                        slug: '/supplier'
                    },
                    {
                        text: localeText?.common?.staff,
                        slug: '/staff'
                    }
                ]
            },

            {
                text: localeText?.common?.lowStockAlert,
                slug: '/low-stock-alert',
                icon: <Icon name="lowstockalert" width={18} height={18}></Icon>
            },
            {
                text: localeText?.common?.warehouse,
                slug: '/warehouses',
                icon: <Icon name="warehouses" width={18} height={18}></Icon>
            },
            {
                text: localeText?.Assets?.others,
                slug: '/others',
                icon: <Icon name="others-icon" width={18} height={18}></Icon>
            },

            {
                text: localeText?.common?.syncWithTally || "Sync with Tally",
                slug: '/tally-import',
                icon: <RefreshCw size={18} />,
                submenu: [
                    {
                        text: localeText?.common?.importFromTally || "Import from Tally",
                        slug: '/tally-import'
                    },
                    {
                        text: localeText?.common?.exportToTally || "Export to Tally",
                        slug: '/tally-export'
                    }
                ]
            },
        ]
    }, [isMounted, localeText, profile, currentBusiness])

    const [isSubscribed] = getSubscription()
    const dispatch = useDispatch()

    const currentBusinessName = businesses.find((i) => i.businessId === getCurrentBusiness())

    const handClickItem = (slug: string) => {
        if (slug === '') {
            return
        }

        router.push(`/business${slug}`)
    }

    const haveAccessToManageStaff = () => {
        return currentBusiness()?.roles === 'owner'
    }

    useEffect(() => {
        const business = currentBusiness()
        if (business)
            $posthog.group('business', String(business.businessId ?? ''), {
                name: business.name,
                category: business.category,
                subscription_package: business.subscription?.productId.split('_')[1] || 'free',
                subscription_start_date: business.subscription?.startDate,
                subscription_expired_date: business.subscription?.endDate
            })
    }, [currentBusiness()])

    const sidebarItems = useMemo(() => {
        return navigationMenu.map((item) => {
            if (
                item.slug === '/report' &&
                !checkAccess('readAccountingReport') &&
                !checkAccess('readStockReport') &&
                !checkAccess('readCustomerReport') &&
                !checkAccess('readDailyReport')
            ) {
                return null
            }
            // Hide Sync with Tally for non-owners or users with expired trial and no subscription
            if (item.slug === '/tally-import') {
                const isOwner = currentBusiness()?.roles === 'owner'
                const [isSubscribed] = getSubscription()
                const isTrialValid = !isFreeTimeLimitExceeded(14)
                if (!isOwner || (!isSubscribed && !isTrialValid)) {
                    return null
                }
            }
            return (
                <SidebarListItem
                    active={item.slug === parentRoute || (item.submenu?.some((sub) => sub.slug === parentRoute) ?? false)}
                    key={item.slug}
                    icon={item.icon}
                    shrink={active}
                    onClick={() => {
                        if (item.slug === '/contact') {
                            return
                        }
                        if (!active) {
                            setActive(true)
                        }
                        handClickItem(item.slug)
                    }}
                    subitem={
                        (item.submenu &&
                            (item.submenu.map((subitem) => {
                                if (subitem.slug === '/product-daily-movement' && !checkAccess('readDailyReport')) return null
                                if (subitem.slug === '/report' && !checkAccess('readAccountingReport')) return null
                                if (subitem.slug === '/stock-report' && !checkAccess('readStockReport')) return null
                                if (subitem.slug === '/customer-report' && !checkAccess('readCustomerReport')) return null
                                if (subitem.slug === '/staff' && !haveAccessToManageStaff()) return null
                                return (
                                    <SidebarListSubitem
                                        active={subitem.slug === parentRoute}
                                        key={subitem.slug}
                                        onClick={() => {
                                            if (!active) {
                                                setActive(true)
                                            }
                                            handClickItem(subitem.slug)
                                        }}>
                                        {subitem.text}
                                    </SidebarListSubitem>
                                )
                            }).filter(Boolean) as JSX.Element[])) ||
                        undefined
                    }>
                    {item.text}
                </SidebarListItem>
            )
        })
    }, [navigationMenu, parentRoute, active, checkAccess, haveAccessToManageStaff, currentBusiness, getSubscription, isFreeTimeLimitExceeded])

    if (window.innerWidth < responsiveBreakpointMap.md && !showMobile) {
        return (
            <>
                <DesktopPrompt localeText={localeText}></DesktopPrompt>
            </>
        )
    }

    return (
        <main className="w-full h-full bg-neutrals-100 flex flex-col">
            <div className="flex-grow  flex overflow-hidden relative">
                {!hideNavigation && (
                    <div className="relative h-full flex flex-shrink-0 z-20">
                        <Sidebar
                            shrink={active}
                            showMobileSidebar={showMobileSidebar}
                            prepend={
                                <div
                                    className={`h-[70px] py-5  flex justify-center items-center ${active ? 'px-2 mb-2' : 'px-4'
                                        }  border-b border-neutrals-100`}>
                                    {!active ? (
                                        <Select
                                            value={String(getCurrentBusiness())}
                                            onOpenChange={() => setActive(false)}
                                            onValueChange={(value) => {
                                                const businessIndex = businesses.findIndex((item) => Number(item.businessId) === Number(value))
                                                const selectedBus = businesses[businessIndex]

                                                if (businessIndex !== 0 && selectedBus) {
                                                    const hasSubscription = selectedBus.subscription && !dayjs().isAfter(dayjs(selectedBus.subscription.endDate).local())
                                                    const isTrialActive = selectedBus.createdAt && !dayjs().isAfter(dayjs(selectedBus.createdAt).add(7, 'day'))

                                                    if (!hasSubscription && !isTrialActive) {
                                                        openPaywall(Number(value))
                                                        return
                                                    }
                                                }

                                                setCurrentBusiness(Number(value))
                                                dispatch(
                                                    setCurrencyState({
                                                        currencySymbol: (selectedBus?.currency?.symbol || selectedBus?.currencyId) as string,
                                                        currencyCode: selectedBus?.currency?.currency_code || ""
                                                    })
                                                )
                                                delayedCallback(() => {
                                                    localStorage.removeItem('challanNumber')
                                                    localStorage.removeItem('challanNumberID')
                                                    localStorage.removeItem('invoiceNumber')
                                                    localStorage.removeItem('documentType')

                                                    window.location.href = `/business/dashboard`
                                                }, 250)
                                            }}>
                                            <SelectTrigger className="bg-white font-semibold">
                                                <SelectValue placeholder="Select a business"></SelectValue>
                                            </SelectTrigger>
                                            <SelectContent>
                                                <div className="p-1 w-full">
                                                    <Button
                                                        variant="outline"
                                                        className="w-full gap-2"
                                                        onClick={() => router.push('/business')}>
                                                        <LucidePlusCircle size={18}></LucidePlusCircle>
                                                        <p><span>{localeText?.common?.add}</span></p>
                                                    </Button>
                                                </div>
                                                <SelectSeparator></SelectSeparator>
                                                {businesses.map((item) => (
                                                    <SelectItem key={item.businessId} value={String(item.businessId)}>
                                                        <div className="flex items-center truncate flex-grow flex-shrink ">
                                                            {item.imageUrl && item.imageUrl.startsWith('https') ? (
                                                                <div className="w-5 h-5 mr-2 rtl:mr-0 rtl:ml-2 rounded-full flex items-center justify-center overflow-hidden bg-neutrals-200 flex-shrink-0">
                                                                    <Image src={item.imageUrl} width={20} height={20} alt="" />
                                                                </div>
                                                            ) : (
                                                                <div className="flex-shrink-0 w-5 h-5 mr-2 rtl:mr-0 rtl:ml-2 bg-primary-100 rounded-full"></div>
                                                            )}
                                                            <span className="truncate">{item.name}</span>
                                                        </div>
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    ) : (
                                        <div
                                            className={`w-10 h-10 flex justify-center border border-gray-200 items-center cursor-pointer ${currentBusinessName?.imageUrl && currentBusinessName.imageUrl !== "" && currentBusinessName?.imageUrl !== null ? "rounded-full " : "rounded-full"} bg-white hover:bg-gray-100 border border-gray-200  transition-all text-neutrals-600`}
                                            onClick={() => setActive(false)}
                                        >
                                            {
                                                (currentBusinessName?.imageUrl && currentBusinessName.imageUrl !== "" && currentBusinessName?.imageUrl !== null) ?
                                                    <div className='w-6 h-6 border-gray-200  rounded-full flex justify-center items-center text-lg font-bold text-black'>
                                                        <Image src={`${currentBusinessName?.imageUrl}`} width={20} height={20} alt="" className='w-full h-full rounded-full' />
                                                    </div>
                                                    :
                                                    <div className='w-full h-full rounded-full flex justify-center items-center text-lg font-bold text-black'>
                                                        <span>{currentBusinessName?.name?.charAt(0) || ""}</span>
                                                    </div>
                                            }

                                        </div>
                                    )}
                                </div>
                            }
                            append={
                                <div className={`pb-2 ${active ? 'px-2' : 'px-4'}`}>
                                    <div
                                        onClick={() => setActive(!active)}
                                        className='cursor-pointer text-gray-500 hover:bg-gray-100 transition-all font-medium border mb-2 text-xs border-gray-200 py-0.5 bg-gray-50 flex justify-center items-center rounded-md'>
                                        {
                                            active ?
                                                <ChevronRight className='size-4 text-gray-500 rtl:rotate-180' />
                                                :
                                                <>
                                                    <ChevronLeft className='size-4 text-gray-500 mr-1 rtl:mr-0 rtl:ml-1 rtl:rotate-180' /> <span>{localeText?.common?.collapse || localeText?.stockNew?.Collapse || "Collapse"}</span>
                                                </>
                                        }
                                    </div>
                                    <DropdownWrapper
                                        dropdownMinWidth={200}
                                        onOpen={() => setProfileDropdownState(true)}
                                        onClose={() => setProfileDropdownState(false)}
                                        onSelect={({ value }) => {
                                            if (value === 'logout') {
                                                logout()
                                                return
                                            }

                                            if (value === 'account') {
                                                router.push(`/business/${value}`)
                                                return
                                            }

                                            if (value === 'feedback') {
                                                alert('Feedback option is currently disabled.')
                                            }

                                            if (value === 'contact') {
                                                window.open('https://wa.me/+919929970571', '_blank')
                                            }
                                        }}
                                        options={[
                                            {
                                                text: localeText?.common?.logout,
                                                value: 'logout',
                                                icon: <Icon name="logout"></Icon>
                                            },
                                            {
                                                text: localeText?.common?.setting,
                                                value: 'account',
                                                icon: <Icon name="cog"></Icon>
                                            },
                                            {
                                                text: localeText?.common?.contact_us,
                                                value: 'contact',
                                                icon: <Icon name="chat-outline"></Icon>
                                            }
                                        ]}>
                                        <div
                                            role="button"
                                            className={`flex items-center cursor-pointer rounded-md border mb-2 border-gray-200 ${active ? 'justify-center p-2' : 'px-3 py-1.5'} ${profileDropdownState ? 'bg-[#FF52001A]' : 'hover:bg-[#FF52001A]'
                                                }`}>

                                            <div className={`w-7 h-7 rounded-full flex items-center justify-center overflow-hidden bg-neutrals-100 flex-shrink-0 ${!active && 'mr-3 rtl:mr-0 rtl:ml-3'}`}>
                                                {profile &&
                                                    profile.imageUrl &&
                                                    profile.imageUrl.startsWith('https') &&
                                                    profile.imageUrl.length > 0 && (
                                                        <Image
                                                            src={profile.imageUrl}
                                                            width={28}
                                                            height={28}
                                                            unoptimized
                                                            alt=""
                                                            onError={() =>
                                                                $sentry.sendSentryEvent(
                                                                    'Error loading image: ' + profile.imageUrl
                                                                )
                                                            }
                                                        />
                                                    )}
                                            </div>
                                            {!active && (
                                                <>
                                                    <div className="truncate">
                                                        <span>{profile?.name !== '' ? profile?.name : profile?.phoneNumber}</span>
                                                    </div>
                                                    <Spacer></Spacer>
                                                    <Icon name="dots-vertical" color={COLORS.MAIN.NEUTRALS[400]}></Icon>
                                                </>
                                            )}
                                        </div>
                                    </DropdownWrapper>
                                    {/* {!active && (
                                        <p className="flex justify-center ">
                                            <Link href={'http://simplystock.co'} className="text-sm text-neutrals-400">
                                                simplystock.co
                                            </Link>
                                        </p>
                                    )} */}
                                </div>
                            }>
                            <>
                                {!isSubscribed && (
                                    <div
                                        className={`${active ? 'px-0 justify-center' : 'p-2'
                                            } hover:bg-amber-50 flex items-center rounded-lg`}
                                        role="button"
                                        onClick={() => execFeature()}>
                                        <Icon name="premium-outline" color={COLORS.SEMANTIC.AMBER['600']} size="xl"></Icon>
                                        {!active && (
                                            <>
                                                <div className="w-3"></div>
                                                <p className="font-bold bg-gradient-to-r to-amber-400 from-amber-600 bg-clip-text inline-block text-transparent underline">
                                                    <span>{localeText?.common?.headerDashboard}</span>
                                                </p>
                                            </>
                                        )}
                                    </div>
                                )}
                                {sidebarItems}
                            </>
                        </Sidebar>
                        <div
                            className="absolute -right-3 rtl:-left-3 rtl:right-auto top-10 w-6 h-6 bg-white border border-neutrals-200 rounded-full flex justify-center items-center shadow-sm cursor-pointer hover:bg-neutrals-50 transition-colors z-[100]"
                            onClick={() => setActive(!active)}
                        >
                            <Icon name={active ? 'chevron-right' : 'chevron-left'} className="rtl:rotate-180" size="sm" />
                        </div>
                    </div>
                )
                }

                <div className="bg-white relative flex flex-col overflow-hidden flex-grow" id="right-hand-side">
                    <NewBuildBanner />
                    {children}
                </div>
            </div >
        </main >
    )
}
